import type { JSX } from 'react';
import React, { useState } from 'react';
import validate from 'validate.js';

import type { IBankAccountSource } from '@feathr/blackbox';
import { sourceValidation } from '@feathr/blackbox';
import { Button, Form, Input, toast } from '@feathr/components';

interface IProps {
  onClose: () => void;
  onSave: (source: IBankAccountSource) => Promise<void>;
}

async function getToken(
  props: IProps,
  routing_number: string | null,
  account_number: string | null,
  account_holder_name: string | null,
): Promise<void> {
  try {
    const response = await global.Stripe(STRIPE_API_KEY).createToken('bank_account', {
      country: 'US',
      currency: 'usd',
      routing_number,
      account_number,
      account_holder_name,
      account_holder_type: 'individual',
      // Casting to BankAccountTokenOptions to fix the wrong overload being used.
    } as stripe.BankAccountTokenOptions);

    if (response.error) {
      toast(
        'The information you entered is invalid. Please correct your bank account details and try again.',
        {
          type: 'error',
        },
      );
    } else if (response.token) {
      await props.onSave({
        account_holder_name: response.token.bank_account!.account_holder_name,
        bank_name: response.token.bank_account!.bank_name,
        id: response.token.id,
        last4: response.token.bank_account!.last4,
        status: response.token.bank_account!.status,
      });
    }
  } catch (e) {
    toast('There was an issue adding your bank account.', { type: 'error' });
  }
}

function BankAccountInput(props: IProps): JSX.Element {
  const [routingNumber, setRoutingNumber] = useState<string | null>(null);
  const [accountNumber, setAccountNumber] = useState<string | null>(null);
  const [accountHolderName, setAccountHolderName] = useState<string | null>(null);

  const { onClose } = props;

  const validation =
    validate(
      {
        routing_number: routingNumber,
        account_number: accountNumber,
        account_holder_name: accountHolderName,
      },
      sourceValidation,
    ) || {};

  function handleRoutingNumberChange(newValue?: string): void {
    setRoutingNumber(newValue ?? '');
  }

  function handleAccountNumberChange(newValue?: string): void {
    setAccountNumber(newValue ?? '');
  }

  function handleAccountHolderNameChange(newValue?: string): void {
    setAccountHolderName(newValue ?? '');
  }

  function handleAddBankAccountClick(): void {
    getToken(props, routingNumber, accountNumber, accountHolderName);
  }

  const actions = [
    <Button
      disabled={Object.keys(validation).length > 0}
      key={'add'}
      onClick={handleAddBankAccountClick}
      type={'primary'}
    >
      Add bank account
    </Button>,
    <Button key={'cancel'} onClick={onClose} type={'naked'}>
      Cancel
    </Button>,
  ];

  return (
    <Form actions={actions} label={'Enter ACH details'}>
      <Input
        label={'Routing Number'}
        onChange={handleRoutingNumberChange}
        type={'text'}
        validationError={routingNumber !== null && validation.routing_number}
        value={routingNumber || undefined}
      />
      <Input
        label={'Account Number'}
        onChange={handleAccountNumberChange}
        type={'text'}
        validationError={accountNumber !== null && validation.account_number}
        value={accountNumber || undefined}
      />
      <Input
        label={"Account Holder's Name"}
        onChange={handleAccountHolderNameChange}
        type={'text'}
        validationError={accountHolderName !== null && validation.account_holder_name}
        value={accountHolderName || undefined}
      />
    </Form>
  );
}

export default BankAccountInput;
